import { HoverPopover } from '@app/components/base/HoverPopover';
import { usePlaybackSeekInRange } from '@app/core/playback/hooks/usePlayback';
import { nlpIntegration } from '@mi-front/api-clients';
import { getQueryParam, toTimestamp } from '@mi-front/core';
import { useMemo } from 'react';
import { useActiveStation } from '../../../../../../../core/stations/hooks/useActiveStation';
import { STREAM_UI_CONFIG } from '../../../consts';
import { useStreamSegmentsRange } from '../../../hooks/segments/useStreamSegmentsQuery';
import { useSetCursor } from '../../../stores/stream-screen.store';
import type { EntitiesDisplayMode } from '../entities/TopicEntities';
import { getLiveTopicsDemoMappedToRange } from '../helpers/demo-data';
import { buildTrackSegments } from '../helpers/track-segments.helpers';
import { TRACK_REFETCH_INTERVAL_MS } from '../panel/consts';
import { LiveTopicPopupContent } from './popup/LiveTopicPopupContent';
import { GapSegmentPill } from './track-items/GapSegmentPill';
import { LiveTopicsEmptyStrip } from './track-items/LiveTopicsEmptyStrip';
import { TopicSegmentPill } from './track-items/TopicSegmentPill';

const isLiveTopicsDebug = () => getQueryParam('live-topics-debug') === '1';

const getEntitiesDisplayMode = (): EntitiesDisplayMode =>
  getQueryParam('live-topics-entities') === 'long' ? 'long' : 'short';

const TRACK_HEIGHT = STREAM_UI_CONFIG.focusTracksHeight;

export type { EntitiesDisplayMode } from '../entities/TopicEntities';

/**
 * Live Topics track: renders above the waveform, below the thumbnail roll.
 * Shows pills (display title) per topic with hover popup (title + summary),
 * and "No topics" pills in gaps between topics.
 */
export function LiveTopicsTrack() {
  const { slug } = useActiveStation();
  const range = useStreamSegmentsRange();
  const seekInRange = usePlaybackSeekInRange();
  const setCursor = useSetCursor();
  const fromDatetime = range.from > 0 ? new Date(range.from).toISOString() : '';
  const toDatetime = range.to > 0 ? new Date(range.to).toISOString() : '';
  const debugMode = isLiveTopicsDebug();
  const enabled = !!slug && range.from > 0 && range.to > 0;
  const apiEnabled = enabled && !debugMode;

  const { data, isLoading } = nlpIntegration.useQueryTopicsLiveTopics(
    slug,
    fromDatetime && toDatetime
      ? { from_datetime: fromDatetime, to_datetime: toDatetime }
      : undefined,
    {
      query: { enabled: apiEnabled, refetchInterval: TRACK_REFETCH_INTERVAL_MS },
    },
  );

  const demoTopics = useMemo(
    () => (debugMode && enabled ? getLiveTopicsDemoMappedToRange(range) : null),
    [debugMode, enabled, range.from, range.to],
  );

  const topics = debugMode && demoTopics ? demoTopics : (data ?? []);
  const hasTopics = topics.length > 0;
  const showEmptyState = enabled && !isLoading && !hasTopics;
  const entitiesDisplayMode = getEntitiesDisplayMode();

  const trackSegments = useMemo(
    () => buildTrackSegments(range, topics),
    [range.from, range.to, topics],
  );

  if ((!debugMode && isLoading) || !enabled) {
    return null;
  }

  if (showEmptyState) {
    return <LiveTopicsEmptyStrip />;
  }

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: TRACK_HEIGHT,
        zIndex: 2,
        pointerEvents: 'none',
      }}
    >
      <div style={{ pointerEvents: 'auto', height: '100%' }}>
        {trackSegments.map((segment) => {
          if (segment.type === 'gap') {
            return (
              <GapSegmentPill
                key={`gap-${segment.from}-${segment.to}`}
                from={segment.from}
                to={segment.to}
              />
            );
          }

          return (
            <HoverPopover
              trigger={
                <TopicSegmentPill
                  topic={segment.topic}
                  onClick={() => {
                    const ts = toTimestamp(segment.topic.start_time);
                    setCursor(ts);
                    seekInRange(ts);
                  }}
                />
              }
              key={segment.topic.id}
              positionMode="cursor-x-element-y"
              cursorOffset={{ top: -16 }}
              delay={100}
              contentSx={{ p: 0, maxWidth: 'none' }}
              paperSx={{ minWidth: 320, maxWidth: 480 }}
            >
              <LiveTopicPopupContent
                topic={segment.topic}
                entitiesDisplayMode={entitiesDisplayMode}
              />
            </HoverPopover>
          );
        })}
      </div>
    </div>
  );
}
